
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation('common');

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-teal-50 to-blue-50 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Logo et description */}
          <div>
            <h3 className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              AfricaStudyUSA
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex gap-3 mt-6">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-xl w-5 h-5 flex items-center justify-center text-white"></i>
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors cursor-pointer"
              >
                <i className="ri-twitter-fill text-xl w-5 h-5 flex items-center justify-center text-white"></i>
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill text-xl w-5 h-5 flex items-center justify-center text-white"></i>
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors cursor-pointer"
              >
                <i className="ri-instagram-fill text-xl w-5 h-5 flex items-center justify-center text-white"></i>
              </a>
            </div>
          </div>

          {/* Liens rapides */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">{t('footer.quickLinksTitle')}</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-gray-600 hover:text-teal-600 transition-colors text-sm cursor-pointer"
                >
                  {t('nav.services')}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('process')}
                  className="text-gray-600 hover:text-teal-600 transition-colors text-sm cursor-pointer"
                >
                  {t('nav.process')}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('universities')}
                  className="text-gray-600 hover:text-teal-600 transition-colors text-sm cursor-pointer"
                >
                  {t('nav.universities')}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('testimonials')}
                  className="text-gray-600 hover:text-teal-600 transition-colors text-sm cursor-pointer"
                >
                  {t('nav.testimonials')}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('faq')}
                  className="text-gray-600 hover:text-teal-600 transition-colors text-sm cursor-pointer"
                >
                  {t('nav.faq')}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">{t('footer.contactTitle')}</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-map-pin-line text-teal-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                <span className="text-gray-600 text-sm">Seattle, Washington, USA</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-phone-line text-teal-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                <span className="text-gray-600 text-sm">+1 425 773 6158</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-mail-line text-teal-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                <span className="text-gray-600 text-sm">contact@africastudyusa.com</span>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">{t('footer.servicesTitle')}</h4>
            <ul className="space-y-3">
              <li className="text-gray-600 text-sm">{t('services.items.0.title')}</li>
              <li className="text-gray-600 text-sm">{t('services.items.1.title')}</li>
              <li className="text-gray-600 text-sm">{t('services.items.2.title')}</li>
              <li className="text-gray-600 text-sm">{t('services.items.3.title')}</li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-600 text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>
            © 2024 AfricaStudyUSA. {t('footer.rights')}
          </p>
          <a 
            href="https://readdy.ai/?origin=logo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-gray-600 hover:text-teal-600 text-sm transition-colors cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </div>
    </footer>
  );
}
